import { FC, useState, useMemo, useCallback } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@capacit-com/lara-law-shared-ui";

import { ChevronRight } from "lucide-react";
import { TableOfContentsItem } from "@/models/table-of-contents";
import { cn } from "@/lib/utils";
import { buildTocTree, TocTreeNode } from "@/lib/tocUtils";
import { useTableOfContents } from "@/hooks/useTableOfContents";
import { logger } from "@/lib/logger";

// ---------------------------------------------------------------------------
// Bookmark node (recursive)
// ---------------------------------------------------------------------------

interface TocBookmarkNodeProps {
  node: TocTreeNode;
  onNavigate: (destinationHtmlId: string) => void;
}

const TocBookmarkNode: FC<TocBookmarkNodeProps> = ({ node, onNavigate }) => {
  const { item, children } = node;
  const hasChildren = children.length > 0;
  // Fully expanded by default (LAR-83): users read the whole structure of a law
  // at a glance and collapse the parts they don't need.
  const [isOpen, setIsOpen] = useState(true);

  const content = (
    <div
      className={cn(
        "group/toc-item flex items-start gap-0.5 py-1 px-1 rounded-md hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors min-h-[28px]"
      )}
    >
      {/* Chevron toggle – only rendered when node has children */}
      {hasChildren ? (
        <CollapsibleTrigger asChild>
          <button
            className="shrink-0 p-0.5 rounded hover:bg-accent-foreground/10 transition-transform mt-0.5"
            aria-label={isOpen ? "Skjul underpunkter" : "Vis underpunkter"}
            tabIndex={0}
            id={`toc-toggle-${item.destinationHtmlId}`}
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground group-hover/toc-item:text-sidebar-accent-foreground transition-transform duration-200",
                isOpen && "rotate-90"
              )}
            />
          </button>
        </CollapsibleTrigger>
      ) : (
        // Spacer to keep text alignment consistent
        <span className="w-[18px] shrink-0" />
      )}

      {/* Text label – navigates on click */}
      <button
        className="flex-1 text-left text-xs leading-snug py-0.5 min-w-0 cursor-pointer"
        onClick={() => onNavigate(item.destinationHtmlId)}
        id={`toc-item-${item.destinationHtmlId}`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onNavigate(item.destinationHtmlId);
          }
        }}
      >
        <span className={cn(item.depth === 0 && "font-medium")}>
          {((item.sourceText?.trim() ? item.sourceText : item.destinationText) ?? "").replace(/\u00a0/g, " ")}
        </span>
      </button>
    </div>
  );

  if (!hasChildren) {
    return content;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      {content}
      <CollapsibleContent>
        <div className="pl-3 border-l border-border/40 ml-[9px]">
          {children.map((child) => (
            <TocBookmarkNode
              key={child.item.destinationHtmlId}
              node={child}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------

interface TableOfContentsTabProps {
  blobId?: string;
  /** Optional pre-loaded items (e.g. mock data for dev preview). When provided, overrides the hook. */
  tocItems?: TableOfContentsItem[];
}

const TableOfContentsTab: FC<TableOfContentsTabProps> = ({
  blobId,
  tocItems,
}) => {
  const { items: fetchedItems, isLoading, error } = useTableOfContents(blobId);

  // Use explicit tocItems if provided, otherwise use hook data
  const items = useMemo(() => {
    if (tocItems && tocItems.length > 0) {
      return tocItems;
    }
    return fetchedItems;
  }, [tocItems, fetchedItems]);

  // Build tree from flat list
  const tree = useMemo(() => buildTocTree(items), [items]);

  const handleNavigate = useCallback((destinationHtmlId: string) => {
    const element = document.getElementById(destinationHtmlId);
    if (!element) {
      // A bookmark pointing at an id the rendered document does not contain is a
      // silent dead link for the user — surface it in App Insights.
      logger.warn("Table of contents bookmark target not found in document", {
        operation: "navigateTableOfContents",
        destinationHtmlId,
        blobId,
      });
      return;
    }

    const scrollContainer = element.closest<HTMLElement>('[class*="overflow-y-auto"], [class*="overflow-auto"]');
    // Find sticky header only within the scroll container so we don't pick up page-level sticky elements
    const stickyHeaderInContainer = scrollContainer?.querySelector<HTMLElement>('[class*="sticky"][class*="top-0"]') ?? null;
    const headerHeight = stickyHeaderInContainer?.getBoundingClientRect().height ?? 0;

    if (scrollContainer) {
      const offsetTop = scrollContainer.scrollTop + (element.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top) - headerHeight;
      scrollContainer.scrollTo({ top: Math.max(0, offsetTop), behavior: "smooth" });
    } else {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [blobId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full p-4">
        <p className="text-sm text-muted-foreground">
          Indlæser indholdsfortegnelse...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center w-full h-full p-4">
        <p className="text-sm text-muted-foreground w-[80%] text-center">
          Fejl ved indlæsning af indholdsfortegnelse
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="mb-2 px-2 flex items-center justify-between">
        <h3 className="text-lg font-medium">Indholdsfortegnelse</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {tree.length === 0 ? (
          <div className="flex items-center justify-center w-full h-full p-4">
            <p className="text-sm text-muted-foreground w-[80%] text-center">
              Ingen indholdsfortegnelse tilgængelig
            </p>
          </div>
        ) : (
          // Remount the tree per document revision so each newly opened document
          // starts fully expanded (LAR-83). The workspace viewer keeps this tab
          // mounted while navigating between documents, and bookmark ids repeat
          // across documents, so without this a node collapsed in one document
          // would stay collapsed in the next.
          <div className="px-1 mb-2" key={blobId ?? "toc"}>
            {tree.map((rootNode) => (
              <TocBookmarkNode
                key={rootNode.item.destinationHtmlId}
                node={rootNode}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableOfContentsTab;
